import React, { useEffect, useRef } from 'react';
import { Form, useClassNameMapper } from 'react-bootstrap';
import type { FieldValues } from 'react-hook-form';
import type { FieldPath } from 'react-hook-form/dist/types/path';
import ComponentHelper from '../../../helpers/ui/ComponentHelper/ComponentHelper';
import type { FormFieldVariant } from '../enums';
import { FormInputFieldViewVariant } from '../enums';
import FormField from '../FormField/FormField';
import type { FormFieldProps, FormFieldType } from '../types';
import { FieldType } from '../types';

/**
 * Text area specification to render the text area on the form. NameT is the name of the field and DataT is the
 * definition of bean that holds all the fields for the form.
 */
export interface TextAreaSpec<NameT extends FieldPath<DataT>, DataT extends FieldValues>
  extends FormFieldType<NameT, DataT, FormFieldVariant.TEXT_AREA> {
  /**
   * Number of rows to render for the text area.
   */
  rows?: number;
  /**
   * Whether the field should autogrow or not.
   */
  autoResize?: boolean;

  /**
   * Specifies view variant for text area.
   */
  viewVariant?: FormInputFieldViewVariant;
  /**
   * aria label for text area
   */
  ariaLabel?: string;
  /**
   * Specifies the class name for text area
   */
  textAreaClassName?: string;
  /**
   * Specifies the placeholder message for text area
   */
  placeHolderText?: string;
  /**
   * Specifies whether the label should be removed or not
   */
  removeLabel?: boolean;
}

/**
 * Renders text area field for the form
 *
 * @author Manish Shrestha
 */
const TextAreaField = <NameT extends FieldPath<DataT>, DataT extends FieldValues>({
  fieldSpec,
  formId,
  i18n,
  className,
  formMethods,
  onFormSubmit,
  submitOnChange,
  allFields
}: FormFieldProps<NameT, DataT, TextAreaSpec<NameT, DataT>>): React.ReactElement => {
  const cx = useClassNameMapper();
  const {
    formState: { errors }
  } = formMethods;
  const { formatMessage, hasMessage, loading: textLoading } = i18n;
  const placeHolderKey = `${formId}.${fieldSpec.name}.placeholder`;
  const {
    name,
    rows,
    attributes,
    viewVariant,
    focus,
    focusHandler,
    disabled,
    autoResize,
    ariaLabel,
    textAreaClassName,
    placeHolderText,
    removeLabel
  } = fieldSpec;

  const placeHolderMessage =
    placeHolderText ?? (hasMessage(placeHolderKey) ? formatMessage(placeHolderKey) : undefined);

  const hasBeenFocused = useRef(false);
  let textAreaReference;

  const onChangeHandler = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ): void => {
    if (autoResize) {
      event.target.style.height = 'inherit';
      if (event.target.scrollHeight > event.target.clientHeight) {
        event.target.style.height = `${event.target.scrollHeight}px`;
      }
    }
  };

  useEffect(() => {
    if (
      textAreaReference &&
      autoResize &&
      textAreaReference.style.height !== `${textAreaReference.scrollHeight}px`
    ) {
      textAreaReference.style.height = `${textAreaReference.scrollHeight}px`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  if (textLoading) {
    return null;
  }

  const adjustedFieldSpec: FormFieldType<NameT, DataT, FormFieldVariant.TEXT_AREA> = {
    ...fieldSpec,
    formGroupSpec: {
      ...fieldSpec.formGroupSpec,
      ...(removeLabel && { label: false })
    }
  };

  return (
    <FormField
      fieldSpec={adjustedFieldSpec}
      fieldType={FieldType.UNCONTROLLED}
      formId={formId}
      i18n={i18n}
      formMethods={formMethods}
      onFormSubmit={onFormSubmit}
      submitOnChange={submitOnChange}
      allFormFields={allFields}
      className={cx(
        'lia-g-form-field-no-resize',
        {
          'lia-g-form-field-no-border': viewVariant === FormInputFieldViewVariant.NO_BORDER
        },
        className
      )}
    >
      {({
        render: {
          register: { ref, onBlur },
          onChange
        }
      }) => {
        return (
          <Form.Control
            name={name}
            as="textarea"
            rows={rows ?? 5}
            placeholder={placeHolderMessage}
            ref={(element): void => {
              textAreaReference = element;
              ref(element);
              if (focus) {
                ComponentHelper.focusHandler(hasBeenFocused, element, focusHandler);
              }
            }}
            className={cx(textAreaClassName)}
            data-testid={`TextAreaField.${formId}.${name}`}
            aria-label={ariaLabel || name}
            isInvalid={errors[name] !== undefined}
            onBlur={onBlur}
            onChange={event => {
              onChangeHandler(event);
              onChange(event);
            }}
            disabled={disabled}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...attributes}
          />
        );
      }}
    </FormField>
  );
};

export default TextAreaField;
